import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link, useSearchParams } from 'react-router';
import { useSelector } from 'react-redux';
import styled from 'styled-components';
import { boutiqueItemsAPI } from '../../api/boutiqueItemsAPI';
import { analyticsAPI } from '../../api/itemEngagementAPI';
import { trackItemClicked } from '../../utils/analytics';
import { formatItemPriceEgp } from '../../utils/formatPrice';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import FeaturedStrip from '../../components/items/FeaturedStrip';
import CategoryCarousel from '../../components/items/CategoryCarousel';
import { DESIGNER_BROWSE_CATEGORIES } from '../../constants/designerBrowseCategories';
import BrowseFiltersToolbar, { buildBrowseFetchParams } from '../../components/items/BrowseFiltersToolbar';
import { EMPTY_BROWSE_FILTERS, SHOP_DEPARTMENTS } from '../../constants/itemBrowseFilters';
import VerticalPageSeo from '../../components/seo/VerticalPageSeo';
import { getSectionBrand } from '../../constants/sectionBrands';
import { canOwnerManageShopInPortal } from '../../utils/shopOwnerPortal';
import { getBoutiqueItemPublicPath } from '../../utils/itemLinks';
import ItemFavoriteButton from '../../components/items/ItemFavoriteButton';
import { resolveListingBrand } from '../../components/items/ListingDetailShared';
import { FAVORITE_KIND } from '../../utils/favoriteKeys';
import { getApproximateViewerCoordinates } from '../../utils/viewerGeo';

const BoutiquesPageShell = styled.div`
  min-height: calc(100vh - 120px);
  background:
    radial-gradient(ellipse 90% 55% at 50% -10%, rgba(45, 212, 191, 0.16), transparent 55%),
    linear-gradient(180deg, #e6eeec 0%, #dce8e5 48%, #d2e0dc 100%);
`;

const BoutiquesContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0.5rem 1rem 3rem;
`;

const LocalShopsOneLine = styled.p`
  margin: 0 auto 1rem;
  text-align: center;
  font-size: 0.72rem;
  font-weight: 600;
  color: var(--text-secondary);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const ItemsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 1.5rem;
  @media (max-width: 768px) {
    grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
    gap: 1rem;
  }
`;

const ItemCard = styled(Link)`
  display: block;
  text-decoration: none;
  color: inherit;
  cursor: pointer;
  background: transparent;
  overflow: hidden;
  border: none;
  transition: opacity 0.15s ease;
  &:hover {
    opacity: 0.92;
  }
`;

const ItemImage = styled.div`
  width: 100%;
  aspect-ratio: 1;
  background: var(--bg-secondary);
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--text-light);
  font-size: 3rem;
  position: relative;
  overflow: hidden;

  > img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    object-position: center;
    display: block;
    transform: scale(1.1);
  }
`;

const ItemContent = styled.div`
  padding: 0.55rem 0.15rem 0.25rem;
`;

const ItemPrice = styled.div`
  font-size: 1rem;
  font-weight: 700;
  color: var(--text-primary);
  margin-bottom: 0.2rem;
`;

const ItemMeta = styled.div`
  display: flex;
  align-items: center;
  gap: 0.35rem;
  font-size: 0.75rem;
  color: var(--text-light);
  min-width: 0;
`;

const MetaBrand = styled.span`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  min-width: 0;
`;


const NoItemsMessage = styled.div`
  text-align: center;
  padding: 4rem 2rem;
  color: var(--text-secondary);
  h3 {
    font-size: 1.5rem;
    margin-bottom: 0.5rem;
    color: var(--text-primary);
  }
`;

const LoadMoreSentinel = styled.div`
  width: 100%;
  height: 1px;
`;

const IMAGE_BASE = typeof window !== 'undefined' && (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1')
  ? 'http://127.0.0.1:8080'
  : '';

const HeroActions = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 1rem;
  flex-wrap: wrap;
  margin-bottom: 2rem;
`;

const CreateShopButton = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.85rem 1.75rem;
  background: linear-gradient(135deg, #134e4a 0%, #0f766e 100%);
  color: #ecfdf8;
  text-decoration: none;
  border: 1px solid rgba(94, 234, 212, 0.45);
  border-radius: 0.5rem;
  font-weight: 700;
  font-size: 1rem;
  box-shadow: 0 8px 20px rgba(19, 78, 74, 0.22);
  transition: all 0.2s;
  &:hover {
    transform: translateY(-2px);
    border-color: #5eead4;
    box-shadow: 0 10px 24px rgba(19, 78, 74, 0.28);
    color: #fff;
  }
`;

const CustomOrderCard = styled.section`
  background: linear-gradient(135deg, rgba(15, 61, 54, 0.95), rgba(32, 90, 78, 0.92));
  border-radius: 1rem;
  color: #fff;
  margin: 0 0 1.25rem;
  padding: 1.35rem 1.4rem;
  h2 { font-size: 1.45rem; margin: 0.35rem 0 0.45rem; }
  p { color: rgba(255,255,255,0.88); line-height: 1.5; margin: 0 0 1rem; max-width: 42rem; }
`;
const CustomOrderEyebrow = styled.div`
  font-size: 0.72rem; font-weight: 800; letter-spacing: 0.08em; opacity: 0.85;
`;
const CustomOrderActions = styled.div`display: flex; flex-wrap: wrap; gap: 0.65rem;`;
const CustomOrderLink = styled(Link)`
  background: ${(p) => (p.$secondary ? 'transparent' : '#fff')};
  border: 1.5px solid #fff; border-radius: 0.6rem;
  color: ${(p) => (p.$secondary ? '#fff' : '#0f3d36')};
  font-weight: 800; padding: 0.7rem 1rem; text-decoration: none;
`;

const ManageShopButton = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.85rem 1.75rem;
  background: rgba(255, 255, 255, 0.72);
  color: #134e4a;
  border: 2px solid #0f766e;
  text-decoration: none;
  border-radius: 0.5rem;
  font-weight: 700;
  font-size: 1rem;
  transition: all 0.2s;
  &:hover {
    background: #ecfdf8;
    border-color: #134e4a;
  }
`;

/* ─── Featured Boutiques ─────────────────────────────────────────── */
// eslint-disable-next-line no-unused-vars
const FeaturedSection = styled.div`
  margin-bottom: 2rem;
`;

// eslint-disable-next-line no-unused-vars
const FeaturedTrack = styled.div`
  display: flex;
  gap: 1rem;
  overflow-x: auto;
  padding: 0.5rem 0 1rem;
  scrollbar-width: none;
  &::-webkit-scrollbar { display: none; }
`;

// eslint-disable-next-line no-unused-vars
const FeaturedCard = styled(Link)`
  flex-shrink: 0;
  width: 160px;
  background: white;
  border-radius: 1rem;
  box-shadow: 0 2px 8px rgba(0,0,0,0.08);
  border: 1px solid #e5e7eb;
  text-decoration: none;
  overflow: hidden;
  transition: transform 0.2s, box-shadow 0.2s;
  &:hover { transform: translateY(-4px); box-shadow: 0 8px 20px rgba(0,0,0,0.12); }
`;

// eslint-disable-next-line no-unused-vars
const FeaturedCardImage = styled.div`
  width: 100%;
  height: 140px;
  background: #f3f4f6;
  overflow: hidden;
  img { width: 100%; height: 100%; object-fit: contain; object-position: center; }
`;

// eslint-disable-next-line no-unused-vars
const FeaturedCardBody = styled.div`
  padding: 0.6rem 0.75rem 0.75rem;
`;

// eslint-disable-next-line no-unused-vars
const FeaturedCardTitle = styled.p`
  font-size: 0.8rem;
  font-weight: 700;
  color: #1e293b;
  margin: 0 0 0.25rem;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

// eslint-disable-next-line no-unused-vars
const FeaturedCardPrice = styled.p`
  font-size: 0.75rem;
  color: #764ba2;
  font-weight: 600;
  margin: 0;
`;

const BoutiquesPage = ({ brandKey = 'boutiques' }) => {
  const brand = getSectionBrand(brandKey, 'boutiques');
  const [searchParams] = useSearchParams();
  const { isAuthenticated } = useSelector(s => s.auth);
  const [items, setItems] = useState([]);
  const [myShop, setMyShop] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [browseFilters, setBrowseFilters] = useState({ ...EMPTY_BROWSE_FILTERS });
  const [sort, setSort] = useState('');
  const [viewerCoords, setViewerCoords] = useState(null);
  const [page, setPage] = useState(1);
  const [hasNext, setHasNext] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  const loadMoreRef = useRef(null);
  const loadMoreObserverRef = useRef(null);

  const boutiqueImpSent = useRef(new Set());
  const boutiqueObserverRef = useRef(null);

  const boutiqueCardRef = useCallback((node) => {
    if (!node) return;
    if (!boutiqueObserverRef.current) {
      boutiqueObserverRef.current = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              const id = Number(entry.target.dataset.designerItemId);
              if (id && !boutiqueImpSent.current.has(id)) {
                boutiqueImpSent.current.add(id);
                analyticsAPI.recordImpression(id, 'boutique_item');
              }
            }
          });
        },
        { threshold: 0.5 },
      );
    }
    boutiqueObserverRef.current.observe(node);
  }, []);

  useEffect(() => {
    return () => {
      analyticsAPI.flushImpressions();
      boutiqueObserverRef.current?.disconnect();
    };
  }, []);

  useEffect(() => {
    getApproximateViewerCoordinates().then((c) => {
      if (c) setViewerCoords(c);
    });
  }, []);

  useEffect(() => {
    if (isAuthenticated) {
      boutiqueItemsAPI.getMyShop().then(shop => setMyShop(shop)).catch(() => {});
    }
  }, [isAuthenticated]);

  useEffect(() => {
    const department = searchParams.get('department') || '';
    if (department && !SHOP_DEPARTMENTS.includes(department)) {
      return;
    }
    setBrowseFilters((prev) => (
      prev.department === department ? prev : { ...prev, department }
    ));
  }, [searchParams]);

  const fetchPage = useCallback(async (targetPage, replace = false) => {
    const geoExtra = viewerCoords
      ? { user_lat: viewerCoords.lat, user_lon: viewerCoords.lon }
      : {};
    const filters = buildBrowseFetchParams(browseFilters, sort, geoExtra);
    if (replace) {
      setLoading(true);
    } else {
      setLoadingMore(true);
    }
    try {
      const payload = await boutiqueItemsAPI.getBoutiqueItemsPage(filters, targetPage, 24);
      const nextResults = Array.isArray(payload?.results) ? payload.results : [];
      setItems(prev => (replace ? nextResults : [...prev, ...nextResults]));
      setPage(payload?.page || targetPage);
      setHasNext(Boolean(payload?.has_next));
      setTotalCount(payload?.count || nextResults.length);
    } catch (err) {
      console.error('BoutiquesPage: Failed to fetch boutique items', err);
      if (replace) {
        setItems([]);
        setHasNext(false);
        setTotalCount(0);
      }
    } finally {
      if (replace) setLoading(false);
      else setLoadingMore(false);
    }
  }, [browseFilters, sort, viewerCoords]);

  useEffect(() => {
    fetchPage(1, true);
  }, [fetchPage]);

  useEffect(() => {
    if (!loadMoreRef.current) return;
    if (loadMoreObserverRef.current) loadMoreObserverRef.current.disconnect();
    loadMoreObserverRef.current = new IntersectionObserver(
      (entries) => {
        const first = entries[0];
        if (first?.isIntersecting && hasNext && !loadingMore && !loading) {
          fetchPage(page + 1, false);
        }
      },
      { rootMargin: '300px 0px 300px 0px', threshold: 0.01 }
    );
    loadMoreObserverRef.current.observe(loadMoreRef.current);
    return () => loadMoreObserverRef.current?.disconnect();
  }, [hasNext, loadingMore, loading, page, fetchPage]);

  const handleCategorySelect = (apiCategory) => {
    setBrowseFilters((prev) => ({ ...prev, category: apiCategory, subcategory: '', book_subcategory: '', size: '' }));
  };

  const getImageUrl = (item) => {
    if (item.primary_image) {
      return item.primary_image.startsWith('http') ? item.primary_image : `${IMAGE_BASE}${item.primary_image}`;
    }
    if (item.image_url) return item.image_url;
    if (item.images && item.images.length > 0) {
      const img = item.images[0].image;
      return img.startsWith('http') ? img : `${IMAGE_BASE}${img}`;
    }
    return null;
  };

  if (loading && items.length === 0) {
    return (
      <BoutiquesPageShell>
        <LoadingSpinner text="Loading items..." />
      </BoutiquesPageShell>
    );
  }

  return (
    <BoutiquesPageShell>
    <BoutiquesContainer>
      <VerticalPageSeo showIntro={brandKey !== 'local-shops'} />

      <CategoryCarousel
        categories={DESIGNER_BROWSE_CATEGORIES}
        selectedCategory={browseFilters.category || ''}
        onSelectCategory={handleCategorySelect}
        variant="boutique"
      />

      {brandKey === 'local-shops' && brand.pageDescription ? (
        <LocalShopsOneLine>{brand.pageDescription}</LocalShopsOneLine>
      ) : null}
      {/* CTA buttons */}
      <HeroActions>
        {canOwnerManageShopInPortal(myShop) ? (
          <ManageShopButton to={`/boutique-shop/${boutiqueItemsAPI.buildShopRef(myShop)}`}>
            {brand.myShop}
          </ManageShopButton>
        ) : (
          <CreateShopButton
            to={brandKey === 'local-shops' ? '/create-boutique?kind=local_store' : '/create-boutique'}
          >
            {brand.createShop}
          </CreateShopButton>
        )}
      </HeroActions>

      <CustomOrderCard>
        <CustomOrderEyebrow>MADE FOR YOU</CustomOrderEyebrow>
        <h2>Request a custom order from a boutique</h2>
        <p>Choose a boutique first, pick what they offer, describe your request, and ask for a quote.</p>
        <CustomOrderActions>
          <CustomOrderLink to="/custom-order">Browse custom boutiques</CustomOrderLink>
          <CustomOrderLink to="/customization-requests" $secondary>My customization requests</CustomOrderLink>
          {canOwnerManageShopInPortal(myShop) ? (
            <CustomOrderLink to="/boutique/customization-settings" $secondary>Manage custom orders</CustomOrderLink>
          ) : null}
        </CustomOrderActions>
      </CustomOrderCard>

      <FeaturedStrip
        scope={brand.featuredScope}
        department={browseFilters.department || ''}
      />

      <BrowseFiltersToolbar
        filters={browseFilters}
        onFiltersChange={setBrowseFilters}
        categories={DESIGNER_BROWSE_CATEGORIES}
        sort={sort}
        onSortChange={setSort}
        hideConditionFilter
      />

      {items.length === 0 ? (
        <NoItemsMessage>
          <h3>No items yet.</h3>
          <p>Try selecting a different category or be the first to list an item!</p>
        </NoItemsMessage>
      ) : (
        <>
          <ItemsGrid>
            {items.map((item) => {
              const imageUrl = getImageUrl(item);
              const hasMultipleImages = item.images && item.images.length > 1;
              return (
                <ItemCard
                  key={item.id}
                  to={getBoutiqueItemPublicPath(item)}
                  onClick={() => {
                    trackItemClicked(item.id, { item_type: 'boutique_item', category: item.category });
                    analyticsAPI.recordClick(item.id, 'boutique_item');
                  }}
                  ref={boutiqueCardRef}
                  data-boutique-item-id={item.id}
                >
                  <ItemImage style={{ position: 'relative' }}>
                    <ItemFavoriteButton
                      itemId={item.id}
                      favoriteKind={FAVORITE_KIND.BOUTIQUE}
                      category={item.category}
                    />
                    {imageUrl ? (
                      <>
                        <img
                          src={imageUrl}
                          alt={item.title}
                          style={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'center', transform: 'scale(1.1)' }}
                          onError={(e) => {
                            e.target.style.display = 'none';
                            if (e.target.nextSibling) e.target.nextSibling.style.display = 'flex';
                          }}
                        />
                        {hasMultipleImages && (
                          <div style={{
                            position: 'absolute', top: '8px', left: '8px',
                            backgroundColor: 'rgba(0,0,0,0.6)', color: 'white',
                            padding: '4px 8px', borderRadius: '12px', fontSize: '0.75rem', fontWeight: '600'
                          }}>
                            📷 {item.images.length}
                          </div>
                        )}
                      </>
                    ) : (
                      <div style={{ display: 'flex', width: '100%', height: '100%', alignItems: 'center', justifyContent: 'center', fontSize: '3rem', color: 'var(--text-light)' }}>
                        📦
                      </div>
                    )}
                  </ItemImage>
                  <ItemContent>
                    <ItemPrice>{formatItemPriceEgp(item.price)}</ItemPrice>
                    <ItemMeta>
                      <MetaBrand>{resolveListingBrand(item) || '—'}</MetaBrand>
                    </ItemMeta>
                  </ItemContent>
                </ItemCard>
              );
            })}
          </ItemsGrid>
          {loadingMore && <LoadingSpinner text="Loading more designs..." />}
          {!hasNext && items.length > 0 && (
            <NoItemsMessage style={{ paddingTop: '1.2rem', paddingBottom: '1.2rem' }}>
              <p>Showing {items.length} of {totalCount} items.</p>
            </NoItemsMessage>
          )}
          <LoadMoreSentinel ref={loadMoreRef} />
        </>
      )}
    </BoutiquesContainer>
    </BoutiquesPageShell>
  );
};

export default BoutiquesPage;
